import { useEffect, useState } from 'react';
import { usePrevious } from '@namespace/helpers';
import {
  CATEGORIES_SHARED_BETWEEN_CASINOS,
  GAMES_LIST_PAGE_SIZE,
  PLEX_LIMIT,
  RECENTLY_PLAYED_CATEGORY
} from '../../constants';
import { gamesStoreActions, useGetIsLobbyDataLoaded } from '../../store';
import { getGames } from '../../utils';
import { useGetIsCustomCategory } from '../useGetIsCustomCategory';
import { useOnLangPlatformChange } from './hooks/useOnLangPlatformChange';
import {
  useAdjustPaginationGamesToCurrentPageNumber,
  useCustomCategoriesData,
  useCustomCategoriesHandlePageChange,
  useGetPlexPaginationCommonData,
  usePlexPaginationLoadGames,
  usePlexPaginationResetPage
} from './paginationHelperHooks';

const { PLEX_SET_PAGINATION } = gamesStoreActions;

export const usePlexCustomCategoryPagination = ({
  casinoType,
  category,
  pageSize = GAMES_LIST_PAGE_SIZE
}) => {
  const { isCustomCategory, isCustomCategoryFlagCalculated } =
    useGetIsCustomCategory(category);

  // getting common data from contexts
  const { gamesData, feedName, plexPagination, userInfoLoaded, formattedPage } =
    useGetPlexPaginationCommonData(casinoType);

  const isLobbyDataLoaded =
    useGetIsLobbyDataLoaded(feedName, casinoType) && userInfoLoaded;
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

  // preparing custom categories data
  // handle favorite custom-category
  const {
    multiSectionCategories,
    actualIsAllGamesLoaded,
    isMultiSectionCategory,
    isFavoriteCategory,
    categoryGameIds,
    plexRequestPageSize,
    categoryGames
  } = useCustomCategoriesData({
    category,
    casinoType,
    pageSize,
    isInitialDataLoaded
  });

  // getting loadGames function
  const {
    loadGames: loadCategoryGames,
    isLoading,
    setIsLoading
  } = usePlexPaginationLoadGames({
    isCustomCategory,
    entity: 'category',
    entityId: category,
    paginationGamesIds: plexPagination.category?.gamesIds,
    casinoType,
    feedName: isFavoriteCategory ? feedName : undefined
  });

  // Reset isInitialDataLoaded state after change language or platform
  useOnLangPlatformChange(() => setIsInitialDataLoaded(false));

  // reset state on category change
  useEffect(() => {
    if (!category || !isCustomCategory) {
      return;
    }
    if (plexPagination.category?.id !== category) {
      setIsInitialDataLoaded(false);
      setIsLoading(false);
    }
  }, [category]);

  const categoryGameIdsLength = categoryGameIds.length;
  const prevCategoryGameIdsLength = usePrevious(categoryGameIdsLength);

  // reset state after games reload
  useEffect(() => {
    if (
      categoryGameIdsLength !== prevCategoryGameIdsLength &&
      isInitialDataLoaded
    ) {
      setIsInitialDataLoaded(false);
      setIsLoading(false);
    }
  }, [categoryGameIdsLength, isInitialDataLoaded]);

  // redirect to first page if no pagination data for category & page > 4
  usePlexPaginationResetPage({
    isNotAllowedToReset:
      !isCustomCategoryFlagCalculated || !category || !isCustomCategory,
    pageSize: plexRequestPageSize,
    games: categoryGames
  });

  // reset custom-pagination only for categories which are shared between casino-types
  useEffect(() => {
    return () => {
      if (
        isCustomCategory &&
        CATEGORIES_SHARED_BETWEEN_CASINOS.includes(category)
      ) {
        PLEX_SET_PAGINATION({
          entity: 'category',
          pagination: {}
        });
      }
    };
  }, [isCustomCategory, casinoType, categoryGameIdsLength]);

  // reset plex pagination for recently played, because recently played are not updated after spins on a new game(now valid only for CC2 because there is no link in the menu on ua13)
  useEffect(() => {
    if (RECENTLY_PLAYED_CATEGORY.id === category) {
      PLEX_SET_PAGINATION({
        entity: 'category',
        pagination: {}
      });
    }
  }, [category]);

  // set initial value
  useEffect(() => {
    (async () => {
      if (
        !isLobbyDataLoaded ||
        isLoading ||
        isInitialDataLoaded ||
        !category ||
        !isCustomCategoryFlagCalculated ||
        !isCustomCategory ||
        !categoryGameIds?.length
      ) {
        return;
      }

      if (
        plexPagination.category?.gamesIds &&
        plexPagination.category?.id === category
      ) {
        PLEX_SET_PAGINATION({
          entity: 'category',
          pagination: {
            id: category,
            gamesIds: plexPagination.category.gamesIds.slice(
              0,
              formattedPage * plexRequestPageSize
            )
          }
        });
        setIsInitialDataLoaded(true);
      } else if (plexRequestPageSize * formattedPage <= PLEX_LIMIT) {
        const gamesToLoad = categoryGameIds.slice(
          0,
          plexRequestPageSize * formattedPage
        );

        await loadCategoryGames({
          offset: 0,
          limit: plexRequestPageSize * formattedPage,
          [isFavoriteCategory ? 'id' : 'launchId']: gamesToLoad,
          isInitialCall: true,
          dontUseGamesStorage: isFavoriteCategory
        });
        setIsInitialDataLoaded(true);
      }
    })();
  }, [
    category,
    categoryGameIds,
    formattedPage,
    isCustomCategory,
    isFavoriteCategory,
    isInitialDataLoaded,
    isLoading,
    isLobbyDataLoaded,
    plexRequestPageSize,
    casinoType
  ]);

  useAdjustPaginationGamesToCurrentPageNumber({
    paginationGames: plexPagination.category.gamesIds,
    formattedPage,
    pageSize: plexRequestPageSize,
    entity: 'category',
    entityId: category,
    adjustIsEnabled:
      category &&
      isCustomCategoryFlagCalculated &&
      isCustomCategory &&
      plexPagination.category?.id === category
  });

  const handlePageChange = useCustomCategoriesHandlePageChange({
    categoryGameIds,
    isFavoriteCategory,
    loadCategoryGames,
    plexRequestPageSize
  });

  return {
    page: formattedPage,
    handlePageChange,
    ...(isMultiSectionCategory
      ? {
          games: undefined
        }
      : {
          games: categoryGames
            ? getGames(categoryGames, gamesData)
            : categoryGames
        }),
    isAllGamesLoaded: actualIsAllGamesLoaded,
    isLoading,
    filteredMultiSectionGames: isMultiSectionCategory
      ? multiSectionCategories[category]?.map((section) => ({
          ...section,
          games: getGames(section.gamesIds, gamesData)
        }))
      : undefined,
    isInitialDataLoaded:
      plexPagination.category?.id &&
      plexPagination.category?.id === category &&
      isInitialDataLoaded
  };
};
